/**
 * As oito telas e as visões de dentro de cada uma.
 *
 * Quem troca de TELA é o menu lateral (grupo E-mail). A faixa de dentro da tela são as **visões
 * daquela tela** — nunca uma segunda cópia do menu. A Geral não tem faixa: ali o que navega é o
 * seletor de modelo do gráfico principal.
 */

export type ScreenKey = 'overview' | 'broadcasts' | 'funnels' | 'templates' | 'routes' | 'offers' | 'leads' | 'clicks';

export interface ScreenView {
    k: string;
    l: string;
    /** Uma linha dizendo o que esta visão mostra; vai no title do botão. */
    d: string;
}

export interface Screen {
    k: ScreenKey;
    /** Segmento da URL, abaixo de /email. */
    path: string;
    l: string;
    /** A pergunta que a tela responde — subtítulo do cabeçalho. */
    q: string;
    views: ScreenView[];
    /** Telas que não usam receita nunca caem no estado "receita não carregada". */
    usesRevenue: boolean;
    /**
     * A lista desta tela é desenhada pelas métricas de envio — e só por isso tem tabela, cards e
     * Personalizar. Cliques conta links e Leads conta pessoas: lá a forma é própria, e um
     * seletor de métricas seria um botão que não muda nada.
     */
    metricList: boolean;
}

export const SCREENS: Screen[] = [
    {
        k: 'overview', path: 'geral', l: 'Geral', q: 'a saúde do canal', usesRevenue: true, metricList: false,
        views: [],
    },
    {
        k: 'broadcasts', path: 'disparos', l: 'Disparos', q: 'qual disparo rendeu', usesRevenue: true, metricList: true,
        views: [
            { k: 'broadcast', l: 'Por disparo', d: 'uma linha por disparo, com as peças dentro' },
            { k: 'piece', l: 'Por peça', d: 'as peças de todos os disparos, soltas' },
            { k: 'ab', l: 'Testes A/B', d: 'só os disparos com teste, A contra B' },
        ],
    },
    {
        k: 'funnels', path: 'funis', l: 'Funis', q: 'qual automação rende', usesRevenue: true, metricList: true,
        views: [
            { k: 'funnel', l: 'Por funil', d: 'uma linha por funil, com os nós dentro' },
            { k: 'node', l: 'Por nó', d: 'os nós que enviam e-mail, de todos os funis' },
        ],
    },
    {
        k: 'templates', path: 'templates', l: 'Templates', q: 'qual modelo performa', usesRevenue: true, metricList: true,
        views: [
            { k: 'template', l: 'Por template', d: 'uma linha por template' },
            { k: 'category', l: 'Por categoria', d: 'agrupado pela categoria do template' },
            { k: 'archived', l: 'Arquivados', d: 'os que saíram do padrão da lista' },
        ],
    },
    {
        k: 'routes', path: 'rotas', l: 'Rotas', q: 'por onde o e-mail sai', usesRevenue: true, metricList: true,
        views: [
            { k: 'route', l: 'Por rota', d: 'uma linha por rota de envio' },
            { k: 'provider', l: 'Por provedor', d: 'somado por provedor e conta' },
            { k: 'shared', l: 'Domínios divididos', d: 'o domínio que mais de uma rota divide' },
        ],
    },
    {
        k: 'offers', path: 'ofertas', l: 'Ofertas', q: 'quanto rendeu cada oferta', usesRevenue: true, metricList: true,
        views: [
            { k: 'all', l: 'Todas as ofertas', d: 'maior receita primeiro' },
            { k: 'attributable', l: 'Com envio atribuível', d: 'as que têm envios e taxas' },
            { k: 'revenueOnly', l: 'Só receita', d: 'sem envio atribuível nesta janela' },
        ],
    },
    {
        k: 'leads', path: 'leads', l: 'Leads', q: 'de onde vêm, e quantos se perdem', usesRevenue: false, metricList: false,
        views: [
            { k: 'period', l: 'No período', d: 'entrada e perda, dia a dia' },
            { k: 'base', l: 'A base de hoje', d: 'a foto de agora — não responde ao período' },
        ],
    },
    {
        k: 'clicks', path: 'cliques', l: 'Cliques', q: 'quais links são mais clicados', usesRevenue: false, metricList: false,
        views: [
            { k: 'link', l: 'Por link', d: 'o top N de links' },
            { k: 'template', l: 'Por template', d: 'somando cliques — contatos únicos não somam' },
        ],
    },
];

export const SCREEN: Record<ScreenKey, Screen> = Object.fromEntries(SCREENS.map((s) => [s.k, s])) as Record<ScreenKey, Screen>;

export const BASE_PATH = '/email';
export const screenPath = (s: Screen) => `${BASE_PATH}/${s.path}`;

/** A permissão das oito telas. Sem ela a API responde 403 em todas. */
export const EMAIL_PERMISSION = 'email.reports.view';

export const defaultView = (k: ScreenKey) => SCREEN[k].views[0]?.k ?? '';
